package com.mgacreative.hotel.feature.admin.ui

import androidx.lifecycle.ViewModel
import androidx.lifecycle.viewModelScope
import com.mgacreative.hotel.core.network.SupabaseNetworkClient
import com.mgacreative.hotel.core.tenant.TenantSession
import io.github.jan.supabase.postgrest.postgrest
import kotlinx.coroutines.flow.MutableStateFlow
import kotlinx.coroutines.flow.StateFlow
import kotlinx.coroutines.flow.asStateFlow
import kotlinx.coroutines.launch
import kotlinx.datetime.Clock
import kotlinx.serialization.SerialName
import kotlinx.serialization.Serializable
import kotlinx.serialization.json.JsonObject
import kotlinx.serialization.json.booleanOrNull
import kotlinx.serialization.json.jsonPrimitive

@Serializable
data class TenantProfile(
    val id: String = "",
    val name: String = "",
    val phone: String? = null,
    val email: String? = null,
    val address: String? = null,
    val social_media: String? = null,
    @SerialName("company_title") val companyTitle: String? = null,
    @SerialName("tax_office") val taxOffice: String? = null,
    @SerialName("tax_number") val taxNumber: String? = null,
    @SerialName("authorized_person") val authorizedPerson: String? = null,
    @SerialName("kbs_provider") val kbsProvider: String? = "EGM",
    @SerialName("kbs_facility_code") val kbsFacilityCode: String? = null,
    @SerialName("kbs_username") val kbsUsername: String? = null,
    @SerialName("kbs_password") val kbsPassword: String? = null,
    @SerialName("kbs_auto_send") val kbsAutoSend: Boolean? = false
)

@Serializable
data class SystemAuditLog(
    val id: String = "",
    val title: String = "",
    val description: String = "",
    val timeAgo: String = "",
    val author: String = ""
)

data class SystemDiagnosticsData(
    val isOnline: Boolean = false,
    val latencyMs: Long? = null,
    val lastSyncText: String = "",
    val databaseStatus: String = "Bağlanıyor...",
    val licenseStatus: String = "Aktif Plan"
)

class AdminViewModel(
    private val supabaseClient: SupabaseNetworkClient,
    private val tenantSession: TenantSession
) : ViewModel() {
    private val _isLoading = MutableStateFlow(false)
    val isLoading: StateFlow<Boolean> = _isLoading.asStateFlow()

    private val _profile = MutableStateFlow<TenantProfile?>(null)
    val profile: StateFlow<TenantProfile?> = _profile.asStateFlow()

    private val _roomCount = MutableStateFlow(0)
    val roomCount: StateFlow<Int> = _roomCount.asStateFlow()

    private val _activeChannels = MutableStateFlow<List<String>>(emptyList())
    val activeChannels: StateFlow<List<String>> = _activeChannels.asStateFlow()

    private val _auditLogs = MutableStateFlow<List<SystemAuditLog>>(emptyList())
    val auditLogs: StateFlow<List<SystemAuditLog>> = _auditLogs.asStateFlow()

    private val _diagnostics = MutableStateFlow(SystemDiagnosticsData())
    val diagnostics: StateFlow<SystemDiagnosticsData> = _diagnostics.asStateFlow()

    fun loadDashboardData() {
        viewModelScope.launch {
            _isLoading.value = true
            val tenantId = tenantSession.currentTenantId ?: tenantSession.sessionState.value.tenantId
            var isConnected = false
            var latency: Long? = null

            try {
                // 1. Tenant Profile
                val startTime = Clock.System.now()
                val profileData = if (!tenantId.isNullOrBlank()) {
                    supabaseClient.client.postgrest["tenants"]
                        .select { filter { eq("id", tenantId) } }
                        .decodeSingleOrNull<TenantProfile>()
                } else {
                    supabaseClient.client.postgrest["tenants"]
                        .select()
                        .decodeList<TenantProfile>()
                        .firstOrNull()
                }
                latency = (Clock.System.now() - startTime).inWholeMilliseconds
                _profile.value = profileData
                isConnected = profileData != null
            } catch (e: Exception) {
                _profile.value = null
            }

            // 2. Real Room Count
            try {
                val rooms = if (!tenantId.isNullOrBlank()) {
                    supabaseClient.client.postgrest["rooms"]
                        .select { filter { eq("tenant_id", tenantId) } }
                        .decodeList<JsonObject>()
                } else {
                    supabaseClient.client.postgrest["rooms"]
                        .select()
                        .decodeList<JsonObject>()
                }
                _roomCount.value = rooms.size
            } catch (e: Exception) {
                _roomCount.value = 0
            }

            // 3. Active Channel Connections
            try {
                val channels = if (!tenantId.isNullOrBlank()) {
                    supabaseClient.client.postgrest["channel_connections"]
                        .select { filter { eq("tenant_id", tenantId) } }
                        .decodeList<JsonObject>()
                } else {
                    supabaseClient.client.postgrest["channel_connections"]
                        .select()
                        .decodeList<JsonObject>()
                }
                _activeChannels.value = channels.filter {
                    it["is_active"]?.jsonPrimitive?.booleanOrNull == true
                }.mapNotNull {
                    it["channel_name"]?.jsonPrimitive?.content
                }
            } catch (e: Exception) {
                _activeChannels.value = emptyList()
            }

            // 4. Real Audit Logs
            try {
                val logs = if (!tenantId.isNullOrBlank()) {
                    supabaseClient.client.postgrest["audit_logs"]
                        .select { filter { eq("tenant_id", tenantId) } }
                        .decodeList<JsonObject>()
                } else {
                    supabaseClient.client.postgrest["audit_logs"]
                        .select()
                        .decodeList<JsonObject>()
                }

                if (logs.isNotEmpty()) {
                    _auditLogs.value = logs.take(5).map { obj ->
                        val action = obj["action"]?.jsonPrimitive?.content ?: "İşlem Kaydı"
                        val details = obj["details"]?.jsonPrimitive?.content ?: "Sistem olayı kaydedildi"
                        val createdAt = obj["created_at"]?.jsonPrimitive?.content ?: ""
                        SystemAuditLog(
                            id = obj["id"]?.jsonPrimitive?.content ?: "",
                            title = action,
                            description = details,
                            timeAgo = formatRelativeTime(createdAt),
                            author = "Sistem"
                        )
                    }
                } else {
                    // Try channel_sync_logs as fallback
                    val syncLogs = if (!tenantId.isNullOrBlank()) {
                        supabaseClient.client.postgrest["channel_sync_logs"]
                            .select { filter { eq("tenant_id", tenantId) } }
                            .decodeList<JsonObject>()
                    } else {
                        supabaseClient.client.postgrest["channel_sync_logs"]
                            .select()
                            .decodeList<JsonObject>()
                    }
                    _auditLogs.value = syncLogs.take(5).map { obj ->
                        val ch = obj["channel_name"]?.jsonPrimitive?.content ?: "Kanal"
                        val msg = obj["message"]?.jsonPrimitive?.content ?: "Senkronizasyon işlemi"
                        val createdAt = obj["created_at"]?.jsonPrimitive?.content ?: ""
                        SystemAuditLog(
                            id = obj["id"]?.jsonPrimitive?.content ?: "",
                            title = "$ch Senkronizasyonu",
                            description = msg,
                            timeAgo = formatRelativeTime(createdAt),
                            author = "Channel Manager"
                        )
                    }
                }
            } catch (e: Exception) {
                _auditLogs.value = emptyList()
            }

            // 5. Diagnostics update
            _diagnostics.value = SystemDiagnosticsData(
                isOnline = isConnected,
                latencyMs = latency,
                lastSyncText = if (_auditLogs.value.isNotEmpty()) _auditLogs.value.first().timeAgo else "Senkronize",
                databaseStatus = if (isConnected) "Supabase Bulut Aktif" else "Bağlantı Bekleniyor",
                licenseStatus = "Kurumsal (Aktif)"
            )

            _isLoading.value = false
        }
    }

    private fun formatRelativeTime(isoTimestamp: String): String {
        if (isoTimestamp.isBlank()) return "Yeni"
        return try {
            val instant = kotlinx.datetime.Instant.parse(isoTimestamp)
            val now = Clock.System.now()
            val duration = now - instant
            val minutes = duration.inWholeMinutes
            when {
                minutes < 1 -> "Az önce"
                minutes < 60 -> "$minutes dk önce"
                duration.inWholeHours < 24 -> "${duration.inWholeHours} saat önce"
                else -> "${duration.inWholeDays} gün önce"
            }
        } catch (_: Exception) {
            isoTimestamp.take(10)
        }
    }

    fun updateProfile(
        name: String, 
        phone: String, 
        email: String, 
        address: String, 
        socialMedia: String,
        companyTitle: String = "",
        taxOffice: String = "",
        taxNumber: String = "",
        authorizedPerson: String = ""
    ) {
        viewModelScope.launch {
            _isLoading.value = true
            try {
                val tenantId = tenantSession.currentTenantId ?: tenantSession.sessionState.value.tenantId
                if (tenantId != null) {
                    val updateData = TenantProfile(
                        id = tenantId,
                        name = name,
                        phone = phone,
                        email = email,
                        address = address,
                        social_media = socialMedia,
                        companyTitle = companyTitle,
                        taxOffice = taxOffice,
                        taxNumber = taxNumber,
                        authorizedPerson = authorizedPerson
                    )
                    supabaseClient.client.postgrest["tenants"]
                        .update(updateData) {
                            filter { eq("id", tenantId) }
                        }
                    _profile.value = updateData
                }
            } catch (e: Exception) {
                // handle error
            } finally {
                _isLoading.value = false
            }
        }
    }

    fun updateKbsSettings(
        provider: String,
        facilityCode: String,
        username: String,
        password: String,
        autoSend: Boolean,
        onResult: (Boolean, String) -> Unit = { _, _ -> }
    ) {
        viewModelScope.launch {
            _isLoading.value = true
            try {
                val tenantId = tenantSession.currentTenantId ?: tenantSession.sessionState.value.tenantId
                if (tenantId != null) {
                    val current = _profile.value
                    val updated = (current ?: TenantProfile(id = tenantId)).copy(
                        kbsProvider = provider,
                        kbsFacilityCode = facilityCode,
                        kbsUsername = username,
                        kbsPassword = password,
                        kbsAutoSend = autoSend
                    )

                    @Serializable
                    data class KbsSettingsPayload(
                        @SerialName("kbs_provider") val kbsProvider: String,
                        @SerialName("kbs_facility_code") val kbsFacilityCode: String,
                        @SerialName("kbs_username") val kbsUsername: String,
                        @SerialName("kbs_password") val kbsPassword: String,
                        @SerialName("kbs_auto_send") val kbsAutoSend: Boolean
                    )

                    val payload = KbsSettingsPayload(
                        kbsProvider = provider,
                        kbsFacilityCode = facilityCode,
                        kbsUsername = username,
                        kbsPassword = password,
                        kbsAutoSend = autoSend
                    )

                    supabaseClient.client.postgrest["tenants"].update(payload) {
                        filter { eq("id", tenantId) }
                    }
                    _profile.value = updated
                    onResult(true, "KBS ayarları başarıyla kaydedildi.")
                } else {
                    onResult(false, "Aktif otel oturumu bulunamadı.")
                }
            } catch (e: Exception) {
                e.printStackTrace()
                onResult(false, "Kayıt sırasında hata oluştu: ${e.message ?: "Bilinmeyen hata"}")
            } finally {
                _isLoading.value = false
            }
        }
    }

    fun testKbsConnection(
        facilityCode: String,
        username: String,
        onResult: (Boolean, String) -> Unit
    ) {
        viewModelScope.launch {
            if (facilityCode.isBlank()) {
                onResult(false, "Tesis kodu boş olamaz!")
                return@launch
            }
            if (username.isBlank()) {
                onResult(false, "Kullanıcı kodu boş olamaz!")
                return@launch
            }
            // Simüle edilen resmi servis bağlantı testi
            kotlinx.coroutines.delay(800)
            onResult(true, "KBS web servis bağlantısı başarılı! Tesis Kodu: $facilityCode doğrulandı.")
        }
    }
}
